import { ArrowUpRightIcon, LockIcon, PlusIcon, SearchIcon } from "lucide-react"
import { useRef, useState, type ComponentProps, type ReactElement, type ReactNode } from "react"
import { Link } from "react-router"
import { Button, buttonVariants } from "@/primitives/button.tsx"
import {
  Combobox,
  ComboboxFooter,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
} from "@/primitives/combobox.tsx"
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/primitives/input-group.tsx"
import { Popover, PopoverContent, PopoverTrigger } from "@/primitives/popover.tsx"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/primitives/tooltip.tsx"
import { cn } from "@/primitives/utils"
import { shortcutOf, type ShortcutId } from "@/shortcuts/registry.ts"
import { useShortcut, useShortcuts } from "@/shortcuts/use-shortcut.ts"
import { Kbd } from "./key.tsx"
import { PageSkeleton } from "./page-skeleton.tsx"
import { creatableFrom, groupsOf, isSearchable, matching, type PickerOption } from "./property.ts"
import { useTriggerTooltip } from "./use-trigger-tooltip.ts"

export type { PickerOption } from "./property.ts"

/*
 * A thing's properties, each shown as its value and changed in place: a ticket's Status, priority,
 * type, assignees and Epic in its rail, and the Type and Priority chips of a composer. A property
 * is a quiet button showing its glyph and value; pressing it (or its key) opens a Picker of the
 * choices beneath it.
 */

/**
 * One labelled line of a property list: the label in a narrow column, then its button. The label
 * stays on the first line beside a `multiline` button that runs to more.
 *
 * `opens` is the page of what the value names, as a ticket's Epic, reached by an arrow after the
 * button that opens it in a new browser tab, so the page the property is on stays where it is.
 */
export function PropertyRow({
  label,
  opens,
  children,
}: {
  label: string
  opens?: { href: string; name: string }
  children: ReactNode
}) {
  return (
    <div className="flex min-h-8 items-start gap-2">
      <span className="flex h-8 w-20 shrink-0 items-center text-xs text-muted-foreground">
        {label}
      </span>
      <div className="flex min-h-8 min-w-0 flex-1 items-center">{children}</div>
      {opens && (
        <Tooltip>
          <TooltipTrigger
            render={
              <Link
                to={opens.href}
                target="_blank"
                rel="noopener"
                aria-label={`Open ${opens.name} in a new tab`}
                className={cn(buttonVariants({ variant: "ghost", size: "icon-sm" }), "mt-0.5")}
              />
            }
          >
            <ArrowUpRightIcon />
          </TooltipTrigger>
          <TooltipContent side="left">Open {opens.name} in a new tab</TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}

type PropertyButtonProps = {
  glyph?: ReactNode
  value?: string
  /** Shown faint in place of a value, as "No Epic" or "Nobody". */
  placeholder: string
  /**
   * The value runs onto as many lines as it needs, as the names of a ticket's Labels do, where
   * otherwise it is cut short at one.
   */
  multiline?: boolean
  readOnly?: boolean
  /** The key that opens its Picker; its tooltip names what the button does, and the key. */
  shortcut?: ShortcutId
  /** Something about the value to notice, as a dot with this in its tooltip. */
  warning?: string
  /**
   * What sets the value, when something else does and it can't be changed here, such as the Epic
   * that owns a ticket's Area, OPS-7: shown read only, with a lock and "from OPS-7" after it.
   */
  lockedFrom?: string
}

/*
 * The props a Picker's trigger hands its button (its ref, its handlers, its open state), which a
 * PropertyButton or PropertyChip passes on. Their look is theirs alone.
 */
type TriggerProps = Omit<
  ComponentProps<typeof Button>,
  "className" | "style" | "children" | "variant" | "size" | "render"
>

function Contents({
  glyph,
  value,
  placeholder,
  multiline = false,
}: Pick<PropertyButtonProps, "glyph" | "value" | "placeholder" | "multiline">) {
  return (
    <>
      {/* On several lines, the glyph stays level with the first. */}
      {multiline ? <span className="flex h-5 shrink-0 items-center">{glyph}</span> : glyph}
      {value ? (
        <span
          className={cn(
            "text-foreground",
            multiline ? "min-w-0 text-left break-words" : "truncate",
          )}
        >
          {value}
        </span>
      ) : (
        <span className="truncate text-subtle-foreground">{placeholder}</span>
      )}
    </>
  )
}

const buttonClass = "h-8 w-full min-w-0 justify-start gap-2 px-2 text-ui font-normal"
const multilineClass = "h-auto min-h-8 items-start py-1.5 whitespace-normal"

/**
 * A property's value as a quiet, full-width button, the trigger of its Picker. Read only, it shows
 * the same thing as plain text, for a Member who can't change it. It is one line, cut short where
 * it doesn't fit, unless it is `multiline`.
 */
export function PropertyButton({
  glyph,
  value,
  placeholder,
  multiline = false,
  readOnly = false,
  shortcut,
  warning,
  lockedFrom,
  ...trigger
}: PropertyButtonProps & TriggerProps) {
  // Its Picker hands it `aria-expanded`, so the tooltip knows when the Picker is open.
  const expanded = trigger["aria-expanded"]
  const tip = useTriggerTooltip(expanded === true || expanded === "true")
  const dot = warning && (
    <span
      role="img"
      aria-label={warning}
      className="ml-auto size-1.5 shrink-0 rounded-full bg-warning"
    />
  )
  if (readOnly || lockedFrom)
    return (
      <div
        className={cn(
          "flex w-full min-w-0 gap-2 px-2 text-ui",
          multiline ? "min-h-8 items-start py-1.5" : "h-8 items-center",
        )}
      >
        <Contents glyph={glyph} value={value} placeholder={placeholder} multiline={multiline} />
        {dot}
        {lockedFrom && (
          <span className="ml-auto flex shrink-0 items-center gap-1 text-xs text-subtle-foreground">
            <LockIcon aria-hidden className="size-3" />
            from {lockedFrom}
          </span>
        )}
      </div>
    )

  const contents = (
    <>
      <Contents glyph={glyph} value={value} placeholder={placeholder} multiline={multiline} />
      {dot}
    </>
  )
  const className = cn(buttonClass, multiline && multilineClass)
  const button = <Button variant="ghost" className={className} {...trigger} />
  if (!shortcut && !warning)
    return (
      <Button variant="ghost" className={className} {...trigger}>
        {contents}
      </Button>
    )
  return (
    <Tooltip {...tip.tooltip}>
      <TooltipTrigger render={button} {...tip.trigger}>
        {contents}
      </TooltipTrigger>
      <TooltipContent side="left">
        {warning && <span>{warning}</span>}
        {shortcut && (
          <>
            {warning && <span aria-hidden>·</span>}
            <span>{shortcutOf(shortcut).label}</span>
            <Kbd shortcut={shortcut} />
          </>
        )}
      </TooltipContent>
    </Tooltip>
  )
}

/** A property as a pill, for a composer's row of chips: its glyph and value, opening its Picker. */
export function PropertyChip({
  glyph,
  value,
  placeholder,
  ...trigger
}: Pick<PropertyButtonProps, "glyph" | "value" | "placeholder"> & TriggerProps) {
  return (
    <Button variant="outline" size="sm" className="rounded-full px-2.5 shadow-none" {...trigger}>
      <Contents glyph={glyph} value={value} placeholder={placeholder} />
    </Button>
  )
}

const widths = {
  sm: "min-w-64",
  md: "w-80",
  lg: "w-md",
} as const

type Choice =
  | { mode: "single"; value: string | null; onChange: (id: string) => void }
  | { mode: "multi"; value: readonly string[]; onChange: (ids: string[]) => void }

/**
 * What a Picker can make from a name typed into its search: the words its row offers it in, and
 * what it says while there is nothing to choose from yet.
 */
const creations = {
  label: { offer: "Create label", hint: "Type a name to create a label" },
} as const

/** What a Picker with nothing to choose from at all says, by why it has nothing. */
const empties = {
  "no-repositories":
    "Beacon's GitHub App reaches no repositories yet. An org owner chooses them on GitHub.",
  "github-not-connected": "This stage has no GitHub App yet, so Beacon can't reach GitHub.",
  "github-unavailable": "GitHub couldn't be read just now. Try again in a moment.",
} as const

/** The row that creates, among the choices' ids: no choice's id holds a NUL. */
const createId = "\u0000create"

/*
 * The choices for a property, in a popover under its trigger (`trigger`, usually a PropertyButton
 * or a PropertyChip). `single` picks one and closes; `multi` checks and unchecks, staying open,
 * with an optional sticky `footer` for the caller's confirming action ("Add 3 Members").
 *
 * Past seven choices it offers a search. A choice with a `disabledReason` can't be picked, and says
 * why. `shortcut` opens it from the keyboard; `open` and `onOpenChange` let the caller open or close
 * it too, as a footer's button does once it has acted. While `loading` (choices still being asked
 * for, as a rule's Slack channels are), skeleton rows stand where the choices will be.
 * `groupGlyph` leads each group's heading, as a provider's monogram leads its models. `onSubmit` is
 * what ⌘↵ does while the Picker is open, as the footer's button does, which then shows `Kbd
 * shortcut="submit"`.
 *
 * With `create`, it always has a search, and a name typed there that no choice is called is offered
 * as a last row, "Create label needs-design": choosing it hands the name to `onCreate` and empties
 * the search. What it `makes` holds the row's words.
 *
 * `empty` names what it says when there is nothing to choose from at all, such as why the choices
 * couldn't be read, in place of "No matches", which still answers a search that matches nothing.
 */
export function Picker({
  options,
  searchPlaceholder,
  footer,
  width,
  trigger,
  shortcut,
  open: openProp,
  onOpenChange,
  loading = false,
  groupGlyph,
  onSubmit,
  create,
  empty,
  ...choice
}: Choice & {
  options: readonly PickerOption[]
  searchPlaceholder: string
  footer?: ReactNode
  width: keyof typeof widths
  trigger: ReactElement
  shortcut?: ShortcutId
  open?: boolean
  onOpenChange?: (open: boolean) => void
  loading?: boolean
  groupGlyph?: (heading: string) => ReactNode
  onSubmit?: () => void
  create?: { makes: keyof typeof creations; onCreate: (name: string) => void }
  empty?: keyof typeof empties
}) {
  const [ownOpen, setOwnOpen] = useState(false)
  const open = openProp ?? ownOpen
  const [query, setQuery] = useState("")
  const list = useRef<HTMLDivElement>(null)
  const popup = useRef<HTMLDivElement>(null)
  const searchable = isSearchable(options) || create !== undefined
  const shown = searchable ? matching(options, query) : options
  const creatable = create ? creatableFrom(options, query) : undefined

  function setOpen(next: boolean) {
    if (openProp === undefined) setOwnOpen(next)
    onOpenChange?.(next)
    if (!next) setQuery("")
  }

  /** Makes the name typed, when the row offering it was the one chosen. */
  function created(chosen: readonly (string | null)[]) {
    if (!create || creatable === undefined || !chosen.includes(createId)) return false
    create.onCreate(creatable)
    setQuery("")
    return true
  }

  useShortcuts(shortcut ? { [shortcut]: () => setOpen(true) } : {})
  // The popover is open over the page, so ⌘↵ is bound within it, where the search has focus.
  useShortcut("submit", () => onSubmit?.(), { enabled: open && !!onSubmit, within: popup })

  // The combobox is the list inside the popover, always open while it is: its own popup is the
  // popover's. The search is the Picker's, filtered here, so an empty result is known.
  const shared = {
    inline: true,
    open,
    autoHighlight: true,
    filter: null,
    inputValue: query,
    onInputValueChange: (next: string, { reason }: { reason: string }) => {
      if (reason === "input-change") setQuery(next)
      else if (reason === "input-clear") setQuery("")
    },
  } as const

  const contents = (
    <>
      {searchable && <ComboboxInput variant="search" placeholder={searchPlaceholder} />}
      <ComboboxList ref={list} className="outline-none data-empty:p-1">
        {groupsOf(shown).map(({ heading, options }) => (
          <ComboboxGroup key={heading ?? ""}>
            {heading &&
              (groupGlyph ? (
                <ComboboxLabel className="flex items-center gap-1.5">
                  {groupGlyph(heading)}
                  {heading}
                </ComboboxLabel>
              ) : (
                <ComboboxLabel>{heading}</ComboboxLabel>
              ))}
            {options.map((option) => (
              <ComboboxItem
                key={option.id}
                value={option.id}
                disabled={option.disabledReason !== undefined}
                indicator={choice.mode === "multi" ? "checkbox" : "check"}
                className={cn(option.disabledReason && "h-auto min-h-8 py-1.5")}
              >
                {option.glyph}
                <span className="min-w-0 flex-1">
                  <span className="flex min-w-0 items-baseline gap-1.5">
                    <span className="truncate">{option.label}</span>
                    {option.secondary && (
                      <span className="truncate text-subtle-foreground">{option.secondary}</span>
                    )}
                  </span>
                  {option.disabledReason && (
                    <span className="block text-xs text-muted-foreground">
                      {option.disabledReason}
                    </span>
                  )}
                </span>
                {option.trailing && (
                  <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                    {option.trailing}
                  </span>
                )}
                {option.disabledReason && (
                  <LockIcon aria-hidden className="size-3.5 text-subtle-foreground" />
                )}
              </ComboboxItem>
            ))}
          </ComboboxGroup>
        ))}
        {create && creatable !== undefined && (
          <ComboboxItem value={createId}>
            <PlusIcon aria-hidden className="size-3.5 text-subtle-foreground" />
            <span className="min-w-0 truncate">
              {creations[create.makes].offer} <span className="font-medium">{creatable}</span>
            </span>
          </ComboboxItem>
        )}
      </ComboboxList>
      {loading && <PageSkeleton kind="dialog-list" />}
      {!loading && shown.length === 0 && creatable === undefined && (
        <p className="px-4 py-6 text-center text-ui text-subtle-foreground">
          {options.length > 0
            ? "No matches"
            : create
              ? creations[create.makes].hint
              : empty
                ? empties[empty]
                : "No matches"}
        </p>
      )}
      {footer && <ComboboxFooter>{footer}</ComboboxFooter>}
    </>
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger render={trigger} />
      <PopoverContent
        ref={popup}
        align="start"
        // Without a search to type in, the list itself takes the arrow keys.
        initialFocus={searchable ? true : list}
        className={cn("gap-0 overflow-hidden p-0", widths[width])}
      >
        {choice.mode === "multi" ? (
          <Combobox
            {...shared}
            multiple
            value={[...choice.value]}
            onValueChange={(ids) => {
              if (!created(ids)) choice.onChange(ids)
            }}
          >
            {contents}
          </Combobox>
        ) : (
          <Combobox
            {...shared}
            value={choice.value}
            onValueChange={(id) => {
              if (id !== null && !created([id])) choice.onChange(id)
              setOpen(false)
            }}
          >
            {contents}
          </Combobox>
        )}
      </PopoverContent>
    </Popover>
  )
}

/*
 * The Picker's search row, for a menu that isn't a Combobox, as the Filter menu's list of a facet's
 * values isn't: a search icon, no chrome but a hairline beneath it, 36px tall. The Picker's own is
 * ComboboxInput's `search` shape, which can't stand outside a Combobox (base-ui's Combobox.Input
 * reads the root's context, and throws without it), so this draws the same row around a plain
 * input, class for class with that shape in `@/primitives/combobox.tsx`. Change one, change both.
 */
export function SearchRow(
  props: Omit<ComponentProps<typeof InputGroupInput>, "className" | "style">,
) {
  return (
    <InputGroup
      data-variant="search"
      className="h-9 w-full rounded-none border-0 border-b border-border-subtle bg-transparent px-1 shadow-none hover:border-border-subtle has-[[data-slot=input-group-control]:focus-visible]:border-border-subtle has-[[data-slot=input-group-control]:focus-visible]:ring-0 dark:bg-transparent"
    >
      <InputGroupAddon align="inline-start">
        <SearchIcon />
      </InputGroupAddon>
      <InputGroupInput {...props} />
    </InputGroup>
  )
}
